import { useState, useEffect } from 'react';
import { useAdminAccess } from '../../hooks/useAdminAccess';
import { adminApi, Tenant } from '../../services/adminApi';

export default function TenantManagement() {
  const { isSuperAdmin } = useAdminAccess();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTenant, setNewTenant] = useState({ tenant_id: '', name: '', subdomain: '' });

  useEffect(() => {
    if (isSuperAdmin) {
      loadTenants();
    }
  }, [isSuperAdmin]);

  const loadTenants = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminApi.listTenants();
      setTenants(data.tenants || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async () => {
    if (!newTenant.tenant_id.trim() || !newTenant.name.trim()) return;

    try {
      setCreating(true);
      setError(null);
      await adminApi.createTenant({
        tenant_id: newTenant.tenant_id.trim().toLowerCase(),
        name: newTenant.name.trim(),
        subdomain: newTenant.subdomain.trim().toLowerCase() || newTenant.tenant_id.trim().toLowerCase(),
      });
      setShowCreateModal(false);
      setNewTenant({ tenant_id: '', name: '', subdomain: '' });
      loadTenants();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create tenant');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteTenant = async (tenantId: string) => {
    if (!confirm(`Are you sure you want to deactivate tenant "${tenantId}"?`)) return;

    try {
      await adminApi.deleteTenant(tenantId);
      loadTenants();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete tenant');
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  if (!isSuperAdmin) {
    return (
      <div className="admin-page">
        <div className="admin-page-header">
          <h2>Tenant Management</h2>
          <p className="admin-page-subtitle">Manage organizations and their settings</p>
        </div>
        <div className="admin-placeholder">
          <div className="admin-placeholder-icon">🔒</div>
          <h3>Access Restricted</h3>
          <p>Only Super Admins can manage tenants. Contact your administrator for access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h2>Tenant Management</h2>
        <p className="admin-page-subtitle">Manage all organizations in the system</p>
      </div>

      {error && (
        <div className="admin-error">
          {error}
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      <div className="admin-content-area">
        <div className="admin-toolbar">
          <button
            className="admin-btn admin-btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            + Create Tenant
          </button>
          <button
            className="admin-btn admin-btn-secondary"
            onClick={loadTenants}
            disabled={loading}
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="admin-loading">Loading tenants...</div>
        ) : tenants.length === 0 ? (
          <div className="admin-placeholder">
            <div className="admin-placeholder-icon">🏢</div>
            <h3>No Tenants Found</h3>
            <p>Click "Create Tenant" to add the first organization.</p>
          </div>
        ) : (
          <div className="tenant-grid">
            {tenants.map(tenant => (
              <div key={tenant.tenant_id} className="tenant-card">
                <div className="tenant-card-header">
                  <div className="tenant-logo-area">
                    {tenant.branding?.logo_url ? (
                      <img src={tenant.branding.logo_url} alt={tenant.name} />
                    ) : (
                      <div className="tenant-logo-placeholder">
                        {tenant.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="tenant-info">
                    <h3>{tenant.name}</h3>
                    <span className="tenant-subdomain">{tenant.subdomain}.archiveabyss.com</span>
                  </div>
                  <span className={`status-badge status-${tenant.status}`}>
                    {tenant.status}
                  </span>
                </div>

                <div className="tenant-card-body">
                  <div className="tenant-meta">
                    <span>ID: {tenant.tenant_id}</span>
                    <span>Created: {formatDate(tenant.created_at)}</span>
                  </div>

                  {tenant.branding && (
                    <div className="tenant-colors">
                      <div
                        className="color-swatch"
                        style={{ background: tenant.branding.primary_color }}
                        title="Primary"
                      />
                      <div
                        className="color-swatch"
                        style={{ background: tenant.branding.accent_color }}
                        title="Accent"
                      />
                    </div>
                  )}
                </div>

                <div className="tenant-card-actions">
                  <a
                    href={`https://${tenant.subdomain}.archiveabyss.com`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="admin-btn-sm"
                  >
                    Visit
                  </a>
                  <a
                    href={`/admin/branding?tenant=${tenant.tenant_id}`}
                    className="admin-btn-sm"
                  >
                    Branding
                  </a>
                  <button
                    className="admin-btn-sm admin-btn-danger"
                    onClick={() => handleDeleteTenant(tenant.tenant_id)}
                    disabled={tenant.status === 'inactive'}
                  >
                    Deactivate
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Tenant Modal */}
      {showCreateModal && (
        <div className="admin-modal-overlay" onClick={() => !creating && setShowCreateModal(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <h3>Create New Tenant</h3>

            <div className="form-group">
              <label>Tenant ID (unique identifier)</label>
              <input
                type="text"
                className="admin-input"
                placeholder="e.g., acme"
                value={newTenant.tenant_id}
                onChange={e => setNewTenant({ ...newTenant, tenant_id: e.target.value })}
                disabled={creating}
              />
              <small>Lowercase letters and numbers only</small>
            </div>

            <div className="form-group">
              <label>Company Name</label>
              <input
                type="text"
                className="admin-input"
                placeholder="e.g., Acme Corporation"
                value={newTenant.name}
                onChange={e => setNewTenant({ ...newTenant, name: e.target.value })}
                disabled={creating}
              />
            </div>

            <div className="form-group">
              <label>Subdomain (optional)</label>
              <div className="subdomain-input">
                <input
                  type="text"
                  className="admin-input"
                  placeholder={newTenant.tenant_id || 'subdomain'}
                  value={newTenant.subdomain}
                  onChange={e => setNewTenant({ ...newTenant, subdomain: e.target.value })}
                  disabled={creating}
                />
                <span>.archiveabyss.com</span>
              </div>
              <small>Leave empty to use tenant ID as subdomain</small>
            </div>

            <div className="modal-actions">
              <button
                className="admin-btn admin-btn-secondary"
                onClick={() => setShowCreateModal(false)}
                disabled={creating}
              >
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-primary"
                onClick={handleCreateTenant}
                disabled={creating || !newTenant.tenant_id.trim() || !newTenant.name.trim()}
              >
                {creating ? 'Creating...' : 'Create Tenant'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
